import React, { useState, useEffect, useRef } from 'react';
import { 
  Camera, 
  Sparkles, 
  ChefHat,
  Smartphone,
  Users,
  Star,
  ChevronDown,
  ChevronUp,
  Play,
  Pause,
  RotateCcw,
  CheckCircle,
  Clock,
  Award,
  Zap,
  Heart,
  TrendingUp,
  Menu, 
  X,
  ArrowRight,
  Target,
  Shield,
  Crown,
  Gem
} from 'lucide-react';

// Configuration
const CONFIG = {
  name: "Souschaif",
  tagline: "Your AI Kitchen Companion",
  description: "Transform leftovers into masterpieces with intelligent recipe generation powered by advanced AI vision technology.",
  price: "£4.99/week",
  features: [
    {
      icon: "Camera",
      title: "Smart Recognition",
      description: "Advanced AI scans your fridge in seconds, identifying 2,000+ ingredients with 99.5% accuracy",
      stat: "2,000+ ingredients"
    },
    {
      icon: "Sparkles",
      title: "Intelligent Cooking",
      description: "Personalized recipes that adapt to your taste preferences, dietary restrictions, and cooking skill level",
      stat: "50,000+ recipes"
    },
    {
      icon: "Target",
      title: "Waste Reduction",
      description: "Save money and reduce food waste by using what you already have before it expires",
      stat: "£200/year savings"
    }
  ],
  stats: [
    { value: "2,000+", label: "Ingredients Recognized" },
    { value: "50,000+", label: "Recipe Variations" },
    { value: "30 sec", label: "Average Response Time" },
    { value: "99.5%", label: "Recognition Accuracy" }
  ]
};

// Sample fridge ingredients for simulation
const SAMPLE_INGREDIENTS = [
  "Fresh Salmon Fillet", "Lemon", "Asparagus Spears", "Garlic Cloves",
  "Cherry Tomatoes", "Basil Leaves", "Parmesan Cheese", "Pasta",
  "White Wine", "Olive Oil", "Fresh Herbs", "Red Onion"
];

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [isScannerActive, setIsScannerActive] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [demoIngredients, setDemoIngredients] = useState([]);
  const [currentRecipe, setCurrentRecipe] = useState(0);
  const [isTyping, setIsTyping] = useState(false);

  const scannerRef = useRef(null);
  const heroRef = useRef(null);

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Simulate ingredient scanning
  useEffect(() => {
    if (isScannerActive) {
      const ingredients = [
        "Fresh Salmon", "Lemon", "Asparagus", "Garlic", "Olive Oil",
        "Cherry Tomatoes", "Basil", "Parmesan", "Pasta", "White Wine"
      ];

      let currentIndex = 0;
      const interval = setInterval(() => {
        if (currentIndex < ingredients.length) {
          setDemoIngredients(prev => [...prev, ingredients[currentIndex]]);
          currentIndex++;
          setScanProgress((currentIndex / ingredients.length) * 100);
        } else {
          clearInterval(interval);
          setTimeout(() => {
            setIsScannerActive(false);
            setScanProgress(100);
          }, 1000);
        }
      }, 300);

      return () => clearInterval(interval);
    }
  }, [isScannerActive]);

  // Typing effect for recipe generation
  useEffect(() => {
    if (scanProgress === 100 && demoIngredients.length > 0) {
      setIsTyping(true);
      setTimeout(() => setIsTyping(false), 2000);
    }
  }, [scanProgress, demoIngredients]);

  const recipes = [
    {
      name: "Mediterranean Salmon Pasta",
      time: "25 mins",
      difficulty: "Easy",
      ingredients: demoIngredients,
      steps: ["Sauté garlic in olive oil", "Add tomatoes and herbs", "Cook pasta", "Grill salmon", "Combine everything"]
    }
  ];

  const faqs = [
    {
      question: "How does the AI ingredient recognition work?",
      answer: "Our advanced computer vision technology uses deep learning to analyze your fridge photo, identifying ingredients with 99.5% accuracy. It recognizes packaging, expiration dates, and can even detect partial ingredients."
    },
    {
      question: "What if I have dietary restrictions?",
      answer: "Souschaif adapts to all dietary needs - vegan, vegetarian, gluten-free, keto, allergies, and more. Set your preferences once and get perfectly tailored recipes every time."
    },
    {
      question: "Can I save money with Souschaif?",
      answer: "Users typically save £150-£250 per month by reducing food waste and avoiding unnecessary grocery purchases. Our AI helps you use what you have efficiently."
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely. We use enterprise-grade encryption and never store your personal photos. All processing happens securely in our cloud infrastructure."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50 to-emerald-50">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled ? 'bg-white/90 backdrop-blur-lg shadow-xl border-b border-gray-100' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {CONFIG.name}
              </span>
            </div>

            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-green-600 transition-colors font-medium">Features</a>
              <a href="#demo" className="text-gray-700 hover:text-green-600 transition-colors font-medium">Try It</a>
              <a href="#faq" className="text-gray-700 hover:text-green-600 transition-colors font-medium">FAQ</a>
            </div>

            <div className="flex items-center space-x-4">
              <a href="/s" className="inline-flex items-center px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full hover:shadow-lg hover:scale-105 transition-all duration-200 font-semibold">
                Get Started
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
          </div>
        </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white/95 backdrop-blur-lg border-t border-gray-100">
              <div className="px-4 py-4 space-y-3">
                <a href="#features" className="block text-gray-700 hover:text-green-600 transition-colors font-medium py-2">Features</a>
                <a href="#demo" className="block text-gray-700 hover:text-green-600 transition-colors font-medium py-2">Try It</a>
                <a href="#faq" className="block text-gray-700 hover:text-green-600 transition-colors font-medium py-2">FAQ</a>
                <a href="/s" className="w-full mt-4 inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full hover:shadow-lg transition-all duration-200 font-semibold">
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </div>
            </div>
          )}
          </div>
      </nav>

      {/* Hero Section */}
      <section ref={heroRef} className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 via-emerald-400/20 to-teal-400/20"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-green-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-20 transform -translate-y-1/2 opacity-10">
          <img
            src="/static/images/Fridge Photo Moment.png"
            alt="Kitchen background"
            className="w-96 h-auto rounded-2xl"
          />
        </div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center px-4 py-2 bg-green-50 border border-green-200 rounded-full text-green-700 font-medium text-sm mb-8">
            <Zap className="w-4 h-4 mr-2" />
            AI-Powered Kitchen Revolution
        </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
              {CONFIG.name}
            </span>
            </h1>

          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            {CONFIG.description}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <a href="/s" className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-lg inline-flex items-center">
              Get Started
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
            <a href="/s" className="px-8 py-4 bg-white/80 backdrop-blur-sm border border-gray-200 text-gray-700 rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold text-lg inline-flex items-center">
              Learn More
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {CONFIG.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Revolutionary Features
              </span>
          </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the future of cooking with our cutting-edge AI technology
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {CONFIG.features.map((feature, index) => {
              const IconComponent = { Camera, Sparkles, Target }[feature.icon];
              return (
                <div key={index} className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-2xl transform rotate-1 group-hover:rotate-0 transition-transform duration-300"></div>
                  <div className="relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 text-gray-900">{feature.title}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>
                    <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-full text-green-700 font-semibold">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      {feature.stat}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* AI Showcase */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 md:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  See the AI in Action
                </h3>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Watch our advanced AI technology identify ingredients with incredible precision,
                  learning from every interaction to provide better results over time.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="text-2xl font-bold text-green-600 mb-1">99.5%</div>
                    <div className="text-sm text-gray-600">Accuracy Rate</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="text-2xl font-bold text-green-600 mb-1">30 sec</div>
                    <div className="text-sm text-gray-600">Processing Time</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-xl p-4">
                  <img
                    src="/static/images/AI Magic Showcase.png"
                    alt="AI ingredient recognition showcase"
                    className="w-full h-auto rounded-xl"
                  />
                </div>
                <div className="absolute -top-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                  AI Magic ✨
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="demo" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-green-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                How It Works
              </span>
              </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how our AI transforms your ingredients into delicious recipes
            </p>
            </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Ingredient Scanner */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">AI Ingredient Scanner</h3>
                  <button
                    onClick={() => {
                      setIsScannerActive(!isScannerActive);
                      if (!isScannerActive) {
                        setDemoIngredients([]);
                        setScanProgress(0);
                      } else {
                        // Show all ingredients immediately when starting
                        setDemoIngredients(SAMPLE_INGREDIENTS);
                        setScanProgress(100);
                      }
                    }}
                    className={`px-4 py-2 rounded-full font-semibold transition-all duration-200 ${
                      isScannerActive
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                  >
                    {isScannerActive ? (
                      <>
                        <Pause className="w-4 h-4 mr-2 inline" />
                        Stop
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2 inline" />
                        Start Scan
                      </>
                    )}
                  </button>
                </div>

                {/* Fridge Image Display */}
                <div className="relative bg-gray-100 rounded-xl overflow-hidden mb-6">
                  <div className="aspect-video relative">
                    <img
                      src="/static/images/ingredients.png"
                      alt="Fridge with various ingredients"
                      className="w-full h-full object-cover rounded-xl"
                    />

                    {isScannerActive && (
                      <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center rounded-xl">
                        <div className="text-center">
                          <div className="w-24 h-24 border-4 border-green-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                          <p className="text-white font-semibold">Scanning ingredients...</p>
                          <p className="text-green-200">{Math.round(scanProgress)}% complete</p>
              </div>
            </div>
                    )}

                    {/* AI Recognition overlays */}
                    {isScannerActive && scanProgress === 100 && (
                      <>
                        <div className="absolute top-4 left-4 w-16 h-16 border-2 border-green-400 rounded-lg animate-pulse"></div>
                        <div className="absolute top-4 right-4 w-16 h-16 border-2 border-green-400 rounded-lg animate-pulse" style={{animationDelay: '0.2s'}}></div>
                        <div className="absolute bottom-4 left-4 w-16 h-16 border-2 border-green-400 rounded-lg animate-pulse" style={{animationDelay: '0.4s'}}></div>
                        <div className="absolute bottom-4 right-4 w-16 h-16 border-2 border-green-400 rounded-lg animate-pulse" style={{animationDelay: '0.6s'}}></div>
                      </>
                    )}
          </div>
        </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                  <div
                    className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${scanProgress}%` }}
                  ></div>
                </div>

                {/* Ingredients List */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900 mb-3">Detected Ingredients:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {demoIngredients.map((ingredient, index) => (
                      <div key={index} className="flex items-center space-x-2 bg-green-50 p-2 rounded-lg animate-fade-in-up" style={{animationDelay: `${index * 100}ms`}}>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-green-700">{ingredient}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Recipe Generation */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="flex items-center space-x-2 mb-6">
                <ChefHat className="w-6 h-6 text-green-500" />
                <h3 className="text-2xl font-bold text-gray-900">Recipe Generation</h3>
                {isTyping && (
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
          </div>
                )}
        </div>

              {scanProgress === 100 && demoIngredients.length > 0 ? (
                <div className="space-y-4">
                  <div className="relative rounded-xl overflow-hidden">
                    <img
                      src="/static/images/Recipe Magic Display.png"
                      alt="Generated recipe display"
                      className="w-full h-auto rounded-xl"
                    />
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{recipes[0].name}</h4>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {recipes[0].time}
                      </span>
                      <span className="flex items-center">
                        <Award className="w-4 h-4 mr-1" />
                        {recipes[0].difficulty}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Scan ingredients to generate recipes</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>



      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Frequently Asked Questions
              </span>
          </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about Souschaif
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl border border-gray-100">
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-100 transition-colors duration-200 rounded-xl"
                >
                  <span className="text-lg font-semibold text-gray-900">{faq.question}</span>
                  {activeFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>

                {activeFaq === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Cooking?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Join thousands of home cooks who have revolutionized their kitchen experience with AI-powered cooking assistance.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="/s" className="px-8 py-4 bg-white text-green-600 rounded-full hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-lg inline-flex items-center">
              Get Started
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold">{CONFIG.name}</span>
            </div>
              <p className="text-gray-400 mb-6 max-w-md">
                Revolutionizing home cooking with AI-powered ingredient recognition and personalized recipe generation.
              </p>
              <div className="flex space-x-4">
                <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <span className="text-sm font-bold">F</span>
                </div>
                <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <span className="text-sm font-bold">T</span>
                </div>
                <div className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <span className="text-sm font-bold">I</span>
                </div>
              </div>
            </div>

          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} {CONFIG.name}. All rights reserved.
              </p>
              <div className="flex items-center space-x-6 mt-4 md:mt-0 text-sm text-gray-400">
                <span className="flex items-center">
                  <Shield className="w-4 h-4 mr-2" />
                  SSL Encrypted
                </span>
                <span className="flex items-center">
                  <Gem className="w-4 h-4 mr-2" />
                  Premium Support
                </span>
              </div>
            </div>
          </div>

          {/* Footer Disclaimer */}
          <div className="mt-8 pt-8 border-t border-gray-800">
            <div className="text-center text-gray-400 text-sm space-y-4">
              <p className="leading-relaxed">
                Subscribe and get access to unlimited Souschaif AI recipes. To unsubscribe send STOP to 65065.
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-xs">
                <a href="https://souschaif.com/terms-conditions/" className="hover:text-white transition-colors">Terms & Conditions</a>
                <span className="text-gray-600">|</span>
                <a href="https://souschaif.com/contact/" className="hover:text-white transition-colors">Contact Us</a>
                <span className="text-gray-600">|</span>
                <a href="https://souschaif.com/privacy-policy-souschaif/" className="hover:text-white transition-colors">Privacy Policy</a>
                <span className="text-gray-600">|</span>
                <span className="cursor-pointer hover:text-white transition-colors">Exit</span>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Service provided by Mobixas.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
